import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Maximize2, Calendar } from 'lucide-react';

export type RelevanceMode = 'passage' | 'time-period';

interface TimelineControlsProps {
    // Relevance mode
    relevanceMode: RelevanceMode;
    onRelevanceModeChange: (mode: RelevanceMode) => void;

    // World events filter
    showWorldEvents: boolean;
    onShowWorldEventsChange: (show: boolean) => void;

    // Zoom controls
    onZoomIn: () => void;
    onZoomOut: () => void;
    onFitToPassage: () => void;
}

/**
 * Filter and navigation controls for the timeline
 */
export function TimelineControls({
    relevanceMode,
    onRelevanceModeChange,
    showWorldEvents,
    onShowWorldEventsChange,
    onZoomIn,
    onZoomOut,
    onFitToPassage
}: TimelineControlsProps) {
    return (
        <div className="flex items-center justify-end gap-2 sm:gap-4 overflow-x-auto no-scrollbar py-0.5 w-full -mt-6 sm:-mt-12">
            {/* World Events Toggle - only if passage events only is unchecked */}
            {relevanceMode === 'time-period' && (
                <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={showWorldEvents}
                        onChange={(e) => onShowWorldEventsChange(e.target.checked)}
                        className="rounded border-white/40 bg-white/10 text-primary focus:ring-primary h-3.5 w-3.5 sm:h-4 sm:w-4"
                    />
                    <span className="inline text-white/80 font-medium whitespace-nowrap">
                        <span className="sm:hidden">Broader World</span>
                        <span className="hidden sm:inline">Broader World Events</span>
                    </span>
                </label>
            )}

            {relevanceMode === 'time-period' && (
                <div className="h-4 w-px bg-white/20" />
            )}

            {/* Relevance Mode Toggle */}
            <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm cursor-pointer select-none">
                <input
                    type="checkbox"
                    checked={relevanceMode === 'passage'}
                    onChange={(e) => onRelevanceModeChange(e.target.checked ? 'passage' : 'time-period')}
                    className="rounded border-white/40 bg-white/10 text-primary focus:ring-primary h-3.5 w-3.5 sm:h-4 sm:w-4"
                />
                <span className="inline text-white/80 font-medium whitespace-nowrap">
                    <span className="sm:hidden">Passage Only</span>
                    <span className="hidden sm:inline">Passage Events Only</span>
                </span>
            </label>

            {/* Divider */}
            <div className="h-6 w-px bg-white/20 mx-0.5 sm:mx-0" />

            {/* Zoom Controls */}
            <div className="flex items-center gap-1.5 sm:gap-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onFitToPassage}
                    title="Fit to passage events"
                    className="h-8 px-2 sm:px-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm"
                >
                    <Maximize2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Reset</span>
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onZoomIn}
                    title="Zoom in (±50 years)"
                    className="h-8 w-8 p-0 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm"
                >
                    <ZoomIn className="h-4 w-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onZoomOut}
                    title="Zoom out (±50 years)"
                    className="h-8 w-8 p-0 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm"
                >
                    <ZoomOut className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}

